import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import useProperties from "../../hooks/useProperties";
import useGroupProperties from "../../hooks/useGroupProperties";
import useCities from "../../hooks/useCities";
import useCommunities from "../../hooks/useCommunities";
import useFileUpload from "../../hooks/useFileUpload";
import "./Admin.scss";
import "./PropertyEditor.scss";

const PropertyEditor = () => {
  const { itemType, itemId } = useParams();
  const navigate = useNavigate();
  
  const [cityId, setCityId] = useState(null);
  const [communityId, setCommunityId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [nameInputFocused, setNameInputFocused] = useState(false);
  
  // Helper function to get clean city path without leading/trailing slashes
  const getCleanCityPath = () => {
    // First try to get city from selected cityId
    if (cityId) {
      const cityObj = cities.find(city => city.id === cityId);
      if (cityObj && cityObj.path) {
        return cityObj.path.replace(/^\/+|\/+$/g, ''); // Remove leading and trailing slashes
      }
    }
    
    // If no cityId or city not found, try to extract from property path (for group properties)
    if (formData.path) {
      // Path format is typically: /cityName/communityName/propertyName
      const pathParts = formData.path.split('/').filter(part => part);
      if (pathParts.length > 0) {
        // First part is the city name
        return pathParts[0];
      }
    }
    
    console.error("City not found or city path missing. CityId:", cityId, "Path:", formData.path);
    throw new Error("City path not found. Please select a valid city before uploading or ensure property has a valid path.");
  };

  // Helper function to create a clean file path
  const createCleanPath = (basePath, fileName) => {
    // Remove leading and trailing slashes from basePath
    const cleanBase = basePath.replace(/^\/+|\/+$/g, '');
    
    // Combine with fileName, ensuring a single slash between components
    let result = cleanBase ? `/${cleanBase}/${fileName}` : `/${fileName}`;
    
    // Replace any double slashes with single slashes
    return result.replace(/\/+/g, '/');
  };
  
  // Helper function to properly format file path for DB
  const formatPathForDB = (path) => {
    if (!path) return null;
    
    // Google Drive links are no longer supported, use only local files
    if (path.includes('http') || path.includes('drive.google.com')) {
      console.warn("External links are not supported, ignoring:", path);
      return null;
    }
    
    // Otherwise, ensure it has exactly one leading slash and no double slashes
    return '/' + path.replace(/^\/+/, '').replace(/\/+/g, '/');
  };
  
  // Refs for each section
  const basicInfoRef = useRef(null);
  const linksRef = useRef(null);
  const filesRef = useRef(null);
  const displayRef = useRef(null);
  
  const { cities } = useCities({ autoFetch: true });
  const { communities, fetchCommunities } = useCommunities({ cityId, autoFetch: true });
  
  const { getProperty } = useProperties();
  const { getGroupProperty } = useGroupProperties();
  const { updateProperty } = useProperties();
  const { updateGroupProperty } = useGroupProperties();
  
  const { getFileUrl, uploadPropertyLogo, uploadPropertyFloorplan, uploadPropertyDocument, deletePropertyFile } = useFileUpload();
  
  const [formData, setFormData] = useState({
    name: "",
    desc: "",
    path: "",
    short_desc: "",
    link: "",
    video_link: "",
    logo: "",
    pdf: "",
    coords: [0, 0],
    modalAlign: "right",
    align: "top",
    community_align: "top", // Default value so it's never empty
    docs: "",
    others: "", // Changed from object to string
    community_id: "",
    enabled: true,
    target_zoom: 5 // Default to 5 if not specified
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [docsFile, setDocsFile] = useState(null);
  const [docsPreview, setDocsPreview] = useState(null);
  const [othersFile, setOthersFile] = useState(null);
  const [othersPreview, setOthersPreview] = useState(null);

  // Scroll to section
  const scrollToSection = (ref) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate path from name
  const generatePath = (name) => {
    if (!name) return "";
    
    // Get current path parts
    const pathParts = formData.path.split('/').filter(part => part);
    
    // Generate slug from name
    const slug = name.toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special chars except whitespace and dash
      .replace(/\s+/g, '-')     // Replace whitespace with dash
      .trim();
    
    // If we have at least 2 parts (city and community), update the property part
    if (pathParts.length >= 2) {
      return `/${pathParts[0]}/${pathParts[1]}/${slug}`;
    }
    
    return "";
  };

  // Fetch the property/group property data on component mount
  useEffect(() => {
    const fetchItemData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let itemData;
        
        if (itemType === 'property') {
          itemData = await getProperty(itemId);
        } else if (itemType === 'groupProperty') {
          itemData = await getGroupProperty(itemId);
        } else {
          throw new Error("Invalid item type");
        }
        
        if (itemData) {
          const formattedData = {
            name: itemData.name || "",
            desc: itemData.desc || "",
            path: itemData.path || "",
            short_desc: itemData.short_desc || "",
            link: itemData.link || "",
            video_link: itemData.video_link || "",
            logo: itemData.logo || "",
            pdf: itemData.pdf || "",
            coords: itemData.coords || [0, 0],
            modalAlign: itemData.modalAlign || "right",
            align: itemData.align || "top",
            community_align: itemData.community_align || "top", // Default to top if empty
            docs: itemData.docs || "",
            others: itemData.others || "", // Now directly a string
            community_id: itemData.community_id || "",
            enabled: itemData.enabled !== undefined ? itemData.enabled : true,
            target_zoom: itemData.target_zoom || null // Use null if not specified
          };
          
          setFormData(formattedData);
          setOriginalData(formattedData);
          
          // Log the enabled value for debugging
          console.log("Property enabled value:", itemData.enabled);
          
          // Set city and community IDs for filters
          if (itemData.community_id) {
            setCommunityId(itemData.community_id);
            
            // If we have a community ID, need to find the city ID as well
            const community = communities.find(c => c.id === itemData.community_id);
            if (community) {
              setCityId(community.city_id);
            }
          }
          
          // Set preview URLs if they exist
          if (itemData.logo) {
            setLogoPreview(getFileUrl(itemData.logo));
          }
          
          if (itemData.pdf) {
            setPdfPreview(getFileUrl(itemData.pdf));
          }
          
          // Set preview URL for docs
          if (itemData.docs) {
            setDocsPreview(getFileUrl(itemData.docs));
          }
          
          // Set preview URL for others (now directly a string)
          if (itemData.others) {
            setOthersPreview(getFileUrl(itemData.others));
          }
        } else {
          setError("Could not find the requested item");
        }
      } catch (err) {
        console.error("Error fetching item data:", err);
        setError(err.message || "An error occurred while loading the item data");
      } finally {
        setLoading(false);
      }
    };
    
    if (itemId && itemType) {
      fetchItemData();
    } else {
      setLoading(false);
    }
  }, [itemId, itemType, getProperty, getGroupProperty, getFileUrl, communities]);

  useEffect(() => {
    // Fetch communities when cityId changes
    if (cityId) {
      fetchCommunities();
    }
  }, [cityId, fetchCommunities]);

  // Check for changes when formData, logoFile, or pdfFile changes
  useEffect(() => {
    if (originalData) {
      const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
      const hasFileChanges = logoFile !== null || pdfFile !== null || docsFile !== null || othersFile !== null;
      setHasChanges(hasFormChanges || hasFileChanges);
    }
  }, [formData, logoFile, pdfFile, docsFile, othersFile, originalData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "name") {
      // Update path when name changes
      const newPath = generatePath(value);
      setFormData({
        ...formData,
        [name]: value,
        path: newPath
      });
    } else if (name === "target_zoom") {
      // Handle target_zoom as null when empty
      const newValue = value === "" ? null : parseFloat(value);
      setFormData({
        ...formData,
        [name]: newValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleCoordsChange = (e) => {
    const { name, value } = e.target;
    const coordsIndex = name === "coordX" ? 0 : 1;
    const updatedCoords = [...formData.coords];
    updatedCoords[coordsIndex] = parseInt(value, 10) || 0;
    
    setFormData({
      ...formData,
      coords: updatedCoords,
    });
  };

  const handleFileChange = (e, fileType) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log(`File selected for ${fileType}:`, file.name, file.type, file.size);
      
      if (fileType === 'logo') {
        setLogoFile(file);
        // Create a preview URL
        const previewUrl = URL.createObjectURL(file);
        setLogoPreview(previewUrl);
      } else if (fileType === 'pdf') {
        setPdfFile(file);
        setPdfPreview('New PDF selected');
      } else if (fileType === 'docs') {
        setDocsFile(file);
        setDocsPreview('New PDF selected');
      } else if (fileType === 'others') {
        setOthersFile(file);
        setOthersPreview('New PDF selected');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError(null);
    
    try {
      let itemData = { ...formData };
      console.log("Starting handleSubmit with data:", itemData);
      
      // Upload logo if selected
      if (logoFile) {
        const cityPath = getCleanCityPath();
        console.log(`Uploading logo for property ${itemId} in city ${cityPath}`);
        const logoUpload = await uploadPropertyLogo(logoFile, itemId, cityPath);
        console.log("Logo upload result:", logoUpload);
        if (logoUpload) {
          const extension = logoUpload.filename.substring(logoUpload.filename.lastIndexOf('.'));
          const logoPath = createCleanPath(`/static/${cityPath}/properties/properties-logos`, `${itemId}${extension}`);
          itemData.logo = formatPathForDB(logoPath);
          console.log("Set logo path to:", itemData.logo);
        }
      } else if (formData.logo === null) {
        // Keep null value if it was explicitly set to null (user deleted the logo)
        itemData.logo = null;
        console.log("Set logo to null");
      }
      
      // Upload PDF if selected
      if (pdfFile) {
        const cityPath = getCleanCityPath();
        console.log(`Uploading floorplan for property ${itemId} in city ${cityPath}`);
        const pdfUpload = await uploadPropertyFloorplan(pdfFile, itemId, cityPath);
        console.log("PDF upload result:", pdfUpload);
        if (pdfUpload) {
          const pdfPath = createCleanPath(`/static/${cityPath}/properties/properties-floor-plans`, `${itemId}.pdf`);
          itemData.pdf = formatPathForDB(pdfPath);
          console.log("Set pdf path to:", itemData.pdf);
        }
      } else if (formData.pdf === null) {
        // Keep null value if it was explicitly set to null (user deleted the pdf)
        itemData.pdf = null;
        console.log("Set pdf to null");
      }
      
      // Upload docs PDF if selected
      if (docsFile) {
        const cityPath = getCleanCityPath();
        console.log(`Uploading documents for property ${itemId} in city ${cityPath}`);
        const docsUpload = await uploadPropertyDocument(docsFile, itemId, cityPath, 'docs');
        console.log("Docs upload result:", docsUpload);
        if (docsUpload) {
          const docsPath = createCleanPath(`/static/${cityPath}/properties/properties-documents`, `${itemId}.pdf`);
          itemData.docs = formatPathForDB(docsPath);
          console.log("Set docs path to:", itemData.docs);
        }
      } else if (formData.docs === null) {
        // Keep null value if it was explicitly set to null (user deleted the docs)
        itemData.docs = null;
        console.log("Set docs to null");
      }
      
      // Upload others PDF if selected (now directly a string)
      if (othersFile) {
        const cityPath = getCleanCityPath();
        console.log(`Uploading others for property ${itemId} in city ${cityPath}`);
        const othersUpload = await uploadPropertyDocument(othersFile, itemId, cityPath, 'others');
        console.log("Others upload result:", othersUpload);
        if (othersUpload) {
          const othersPath = createCleanPath(`/static/${cityPath}/properties/properties-others`, `${itemId}.pdf`);
          itemData.others = formatPathForDB(othersPath);
          console.log("Set others path to:", itemData.others);
        }
      } else if (formData.others === null) {
        // Keep null value if it was explicitly set to null (user deleted the others file)
        itemData.others = null;
        console.log("Set others to null");
      }
      
      console.log("Final property data to save:", itemData);
      
      let updatedData;
      if (itemType === 'property') {
        console.log(`Updating property ${itemId} with data:`, itemData);
        updatedData = await updateProperty(itemId, itemData);
        console.log("Update property response:", updatedData);
      } else if (itemType === 'groupProperty') {
        console.log(`Updating group property ${itemId} with data:`, itemData);
        updatedData = await updateGroupProperty(itemId, itemData);
        console.log("Update group property response:", updatedData);
      }
      
      // Update the form data with the returned data from the API (if available)
      if (updatedData) {
        setFormData(updatedData);
        setOriginalData(updatedData);
        console.log("Form data updated with response:", updatedData);
      } else {
        // If no data returned, use the local data we sent
        setFormData(itemData);
        setOriginalData(itemData);
        console.log("No response data, using local data for form update");
      }
      
      // Reset file states after successful upload
      setLogoFile(null);
      setPdfFile(null);
      setDocsFile(null);
      setOthersFile(null);
      
      if (itemData.logo) {
        setLogoPreview(getFileUrl(itemData.logo));
      } else {
        setLogoPreview(null);
      }
      
      if (itemData.pdf) {
        setPdfPreview(getFileUrl(itemData.pdf));
      } else {
        setPdfPreview(null);
      }
      
      if (itemData.docs) {
        setDocsPreview(getFileUrl(itemData.docs));
      } else {
        setDocsPreview(null);
      }
      
      if (itemData.others) {
        setOthersPreview(getFileUrl(itemData.others));
      } else {
        setOthersPreview(null);
      }
      
      // Set hasChanges to false since we've saved
      setHasChanges(false);
      
    } catch (err) {
      console.error("Error saving item:", err);
      setError(err.message || "An error occurred while saving the item");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmLeave) {
        return;
      }
    }
    navigate('/admin/properties');
  };

  // Alignment options for dropdowns
  const alignmentOptions = [
    { value: "top", label: "Top" },
    { value: "top-left", label: "Top Left" },
    { value: "top-right", label: "Top Right" },
    { value: "bottom", label: "Bottom" },
    { value: "bottom-left", label: "Bottom Left" },
    { value: "bottom-right", label: "Bottom Right" },
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p><span role="img" aria-label="loading">⏳</span> Loading property data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2><span role="img" aria-label="error">❌</span> Error</h2>
        <p>{error}</p>
        <button onClick={handleCancel} className="button">
          <span role="img" aria-label="back">⬅️</span> Back to Properties
        </button>
      </div>
    );
  }

  return (
    <div className="property-editor">
      <div className="editor-header">
        <h2><span role="img" aria-label="property">🏢</span> Edit Property: {formData.name || (itemType === 'property' ? 'Property' : 'Group Property')}</h2>
        <button onClick={handleCancel} className="back-button">
          <span role="img" aria-label="back">⬅️</span> Back to Properties
        </button>
      </div>
      
      <div className="editor-container">
        <form onSubmit={handleSubmit} className="editor-form">
          <div className="form-section" ref={basicInfoRef}>
            <h2 className="section-title"><span role="img" aria-label="info">ℹ️</span> Basic Information</h2>
            
            <div className="form-group">
              <label htmlFor="name">Name:</label>
              {nameInputFocused ? (
                <textarea
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onFocus={() => setNameInputFocused(true)}
                  onBlur={() => setNameInputFocused(false)}
                  className="form-control multiline-name"
                  required
                />
              ) : (
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name ? formData.name.replace(/\n/g, ' ') : ''}
                  onChange={handleInputChange}
                  onFocus={() => setNameInputFocused(true)}
                  className="form-control"
                  required
                />
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="short_desc">Short Description:</label>
              <input
                type="text"
                id="short_desc"
                name="short_desc"
                value={formData.short_desc}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="hidden">Hidden in app:</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="hidden"
                  name="enabled" 
                  checked={!formData.enabled}
                  onChange={(e) => {
                    // Use the inverted value of checked for the enabled property
                    setFormData({
                      ...formData,
                      enabled: !e.target.checked,
                    });
                  }}
                  className="toggle-input"
                />
                <label htmlFor="hidden" className="toggle-label">
                  <span className="toggle-inner"></span>
                  <span className="toggle-switch-label">
                    {!formData.enabled ? "Yes" : "No"}
                  </span>
                </label>
              </div>
              <small className="field-info">When checked, this property will be hidden from map, group modals, community pages, header and search</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="desc">Full Description:</label>
              <textarea
                id="desc"
                name="desc"
                value={formData.desc}
                onChange={handleInputChange}
                className="form-control form-textarea"
                rows="5"
              />
            </div>
            
            <div className="form-group">
              <label>Path:</label>
              <div className="read-only-field">
                {formData.path || 'Will be automatically generated from name'}
              </div>
              <small className="field-info">Path is automatically generated from name</small>
            </div>
          </div>
          
          <div className="form-section" ref={filesRef}>
            <h2 className="section-title"><span role="img" aria-label="files">📁</span> Files & Media</h2>
            
            <div className="form-group">
              <label htmlFor="logo">Logo Image:</label>
              {logoPreview && (
                <div className="image-preview">
                  <img 
                    src={logoPreview} 
                    alt="Logo Preview" 
                    className="preview-image"
                  />
                  <button 
                    type="button" 
                    className="remove-file-button"
                    onClick={async () => {
                      const cityPath = getCleanCityPath();
                      console.log(`Deleting logo for property ${itemId} in city ${cityPath}`);
                      const success = await deletePropertyFile(itemId, "logo", cityPath);
                      console.log(`Logo deletion result: ${success ? 'Success' : 'Failed'}`);
                      setLogoFile(null);
                      setLogoPreview(null);
                      setFormData({
                        ...formData,
                        logo: null
                      });
                    }}
                  >
                    <span role="img" aria-label="delete">🗑️</span>
                  </button>
                </div>
              )}
              <input
                type="file"
                id="logo-file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'logo')}
                className="form-control-file"
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="select-file-btn"
                onClick={() => document.getElementById('logo-file').click()}
              >
                <span role="img" aria-label="upload">📷</span> Select Logo
              </button>
            </div>
            
            <div className="form-group">
              <label htmlFor="pdf">Floor Plan PDF:</label>
              {pdfPreview && (
                <div className="file-preview">
                  {typeof pdfPreview === 'string' && pdfPreview === 'New PDF selected' ? (
                    <span>New PDF selected</span>
                  ) : (
                    <div className="pdf-preview-actions">
                      <a href={pdfPreview} target="_blank" rel="noopener noreferrer" className="preview-link">
                        <span role="img" aria-label="view">👁️</span> View
                      </a>
                      <button 
                        type="button" 
                        className="remove-file-button"
                        onClick={async () => {
                          const cityPath = getCleanCityPath();
                          console.log(`Deleting floorplan for property ${itemId} in city ${cityPath}`);
                          const success = await deletePropertyFile(itemId, "floorplan", cityPath);
                          console.log(`Floorplan deletion result: ${success ? 'Success' : 'Failed'}`);
                          setPdfFile(null);
                          setPdfPreview(null);
                          setFormData({
                            ...formData,
                            pdf: null
                          });
                        }}
                      >
                        <span role="img" aria-label="delete">🗑️</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              <input
                type="file"
                id="pdf-file"
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'pdf')}
                className="form-control-file"
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="select-file-btn"
                onClick={() => document.getElementById('pdf-file').click()}
              >
                <span role="img" aria-label="upload">📄</span> Select Floor Plan
              </button>
            </div>
            
            <div className="form-group">
              <label htmlFor="docs-file">Documents PDF:</label>
              {docsPreview && (
                <div className="file-preview">
                  {typeof docsPreview === 'string' && docsPreview === 'New PDF selected' ? (
                    <span>New PDF selected</span>
                  ) : (
                    <div className="pdf-preview-actions">
                      <a href={docsPreview} target="_blank" rel="noopener noreferrer" className="preview-link">
                        <span role="img" aria-label="view">👁️</span> View
                      </a>
                      <button 
                        type="button" 
                        className="remove-file-button"
                        onClick={async () => {
                          const cityPath = getCleanCityPath();
                          console.log(`Deleting documents for property ${itemId} in city ${cityPath}`);
                          const success = await deletePropertyFile(itemId, "documents", cityPath);
                          console.log(`Documents deletion result: ${success ? 'Success' : 'Failed'}`);
                          setDocsFile(null);
                          setDocsPreview(null);
                          setFormData({
                            ...formData,
                            docs: null
                          });
                        }}
                      >
                        <span role="img" aria-label="delete">🗑️</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              <input
                type="file"
                id="docs-file"
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'docs')}
                className="form-control-file"
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="select-file-btn"
                onClick={() => document.getElementById('docs-file').click()}
              >
                <span role="img" aria-label="upload">📑</span> Select Documents
              </button>
            </div>
            
            <div className="form-group">
              <label htmlFor="others-file">Others PDF:</label>
              {othersPreview && (
                <div className="file-preview">
                  {typeof othersPreview === 'string' && othersPreview === 'New PDF selected' ? (
                    <span>New PDF selected</span>
                  ) : (
                    <div className="pdf-preview-actions">
                      <a href={othersPreview} target="_blank" rel="noopener noreferrer" className="preview-link">
                        <span role="img" aria-label="view">👁️</span> View
                      </a>
                      <button 
                        type="button" 
                        className="remove-file-button"
                        onClick={async () => {
                          const cityPath = getCleanCityPath();
                          console.log(`Deleting others for property ${itemId} in city ${cityPath}`);
                          const success = await deletePropertyFile(itemId, "others", cityPath);
                          console.log(`Others deletion result: ${success ? 'Success' : 'Failed'}`);
                          setOthersFile(null);
                          setOthersPreview(null);
                          setFormData({
                            ...formData,
                            others: null
                          });
                        }}
                      >
                        <span role="img" aria-label="delete">🗑️</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              <input
                type="file"
                id="others-file"
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'others')}
                className="form-control-file"
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="select-file-btn"
                onClick={() => document.getElementById('others-file').click()}
              >
                <span role="img" aria-label="upload">📋</span> Select Other Files
              </button>
            </div>
            
            <div className="form-group">
              <label>Gallery Management:</label>
              {itemId && (
                <div className="gallery-management">
                  <Link 
                    to={`/admin/properties/${itemType}/${itemId}/gallery`} 
                    className="gallery-button"
                  >
                    <span role="img" aria-label="gallery">🖼️</span> Manage Gallery
                  </Link>
                  <p className="gallery-info">
                    Click to upload, view, and manage gallery images for this property.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-section" ref={linksRef}>
            <h2 className="section-title"><span role="img" aria-label="links">🔗</span> Links</h2>
            
            <div className="form-group">
              <label htmlFor="link">External Link:</label>
              <input
                type="url"
                id="link"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="video_link">Video Link:</label>
              <input
                type="url"
                id="video_link"
                name="video_link"
                value={formData.video_link}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
          </div>
          
          <div className="form-section" ref={displayRef}>
            <h2 className="section-title"><span role="img" aria-label="display">🖥️</span> Display Settings</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="target_zoom">Target Zoom Level:</label>
                <input
                  type="number"
                  id="target_zoom"
                  name="target_zoom"
                  value={formData.target_zoom === null ? "" : formData.target_zoom}
                  onChange={handleInputChange}
                  className="form-control"
                  min="1"
                  max="18"
                  step="0.1"
                />
                <small className="field-info">Controls the map zoom level when viewing this property (1-18). Used when clicking the map's zoom button or when initially navigating to the property.</small>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="align">Text on map Alignment:</label>
                <select
                  id="align"
                  name="align"
                  value={formData.align}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {alignmentOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="field-info">Controls the position of text labels relative to property markers on the map view.</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="modalAlign">Modal Alignment:</label>
                <select
                  id="modalAlign"
                  name="modalAlign"
                  value={formData.modalAlign}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                </select>
                <small className="field-info">Determines whether property modal windows appear on the left or right side of the screen.</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="community_align">Text on community Alignment:</label>
                <select
                  id="community_align"
                  name="community_align"
                  value={formData.community_align || "top"}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  {alignmentOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="field-info">Controls the position of text labels when this property is displayed within a community view.</small>
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="save-button" disabled={uploading}>
              {uploading ? <span role="img" aria-label="loading">⏳</span> : <span role="img" aria-label="save">💾</span>} {uploading ? "Saving..." : "Save Changes"}
            </button>
          </div>
          
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PropertyEditor; 